import { Typography } from '@mui/material'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import { getFechaFromISO } from 'common/libs/fechas'
import i18n from 'lang/idiomas'
import { useMemo } from 'react'

/**
 * Genera el código de traducción para un campo auxiliar (máximo 8 caracteres)
 * @param {string} campo - Nombre del campo (ej: aux_char01)
 * @returns {string} Código de traducción
 */
export const getTranslationKey = (campo) => {
  return campo
    .toLowerCase()
    .replace('_', '')
    .replace('char', 'c')
    .replace('num', 'n')
    .replace('date', 'd')
}

/**
 * Formatea el valor de un campo auxiliar según su tipo
 * @param {string} campo - Nombre del campo
 * @param {*} valor - Valor del campo
 * @param {string} locale - Locale para formateo
 * @returns {string} Valor formateado
 */
export const formatAuxValue = (campo, valor, locale) => {
  if (valor === null || valor === undefined) return ''

  if (campo.startsWith('aux_date')) {
    return getFechaFromISO(valor, locale?.replace('_', '-')) ?? ''
  }

  if (campo.startsWith('aux_num')) {
    return valor?.toString() ?? ''
  }

  return valor?.toString() ?? ''
}

/**
 * Renderiza los campos auxiliares para una sección y subsección específica
 * @param {Object} props
 * @param {Object} props.otrosDatos - Datos auxiliares del trabajador
 * @param {string} [props.seccion='PER'] - Código de sección (PER, PROF, etc.)
 * @param {string} props.subseccion - Código de subsección (DPER, CONT, PROF, etc.)
 * @param {string} props.locale - Locale para formateo de fechas
 * @param {boolean} [props.inheritColor] - Si debe heredar el color del padre
 */
const CamposAuxiliares = ({
  otrosDatos,
  seccion = 'PER',
  subseccion,
  locale,
  inheritColor = false,
}) => {
  const camposAuxiliares = otrosDatos?.campos_auxiliares ?? {}

  const camposFiltrados = useMemo(() => {
    return Object.entries(camposAuxiliares)
      .filter(([, config]) => config.seccion === seccion && config.subseccion === subseccion)
      .map(([campo, config]) => ({
        campo,
        valor: otrosDatos?.[campo],
        observaciones: config.observaciones,
      }))
  }, [camposAuxiliares, otrosDatos, seccion, subseccion])

  if (camposFiltrados.length === 0) return null

  return camposFiltrados.map(({ campo, valor, observaciones }) => (
    <ListItem key={campo}>
      <ListItemText
        primary={i18n.t(getTranslationKey(campo), { defaultValue: observaciones || campo })}
        secondary={
          inheritColor ? (
            <Typography>{formatAuxValue(campo, valor, locale)}</Typography>
          ) : (
            formatAuxValue(campo, valor, locale)
          )
        }
      />
    </ListItem>
  ))
}

export default CamposAuxiliares
